// adding this as some keys cannot be converted to camelcase
/* eslint-disable camelcase */
import { Button, Typography } from '@vartanainc/design-system';
import { useState } from 'react';
import { ReactComponent as ActivitiesIcon } from '../../assets/activities_icon.svg';
import { ReactComponent as DotIcon } from '../../assets/item_dot.svg';
import { ReactComponent as ChevronIcon } from '../../assets/chevron-right-blue.svg';
import { ReactComponent as CopyIcon } from '../../assets/copy_icon-outlined.svg';
import { ContactTooltipElement } from '../ContactTooltipElement/ContactTooltipElement';
import { formatTimestamp } from '../../utils/helpers';
import colors from '../ColorMap/ColorMap';

interface emailHistoryInterface {
  id: string;
  message: string;
  timestamp: string;
  actual_timestamp: string;
  url?: string;
  trigger_event: boolean;
  recipients: {
    email: string;
    phone?: string;
  }[];
}

interface activitesCardProps {
  emailHistory: emailHistoryInterface[];
  showVendorCheckoutUrl: boolean;
  canCopyLink: boolean;
}

function ActivitiesCard({
  emailHistory,
  showVendorCheckoutUrl,
  canCopyLink,
}: activitesCardProps): JSX.Element {
  const [expandedEmail, setExpandedEmail] = useState<number>(-1);
  const [showCopiedTooltip, setShowCopiedTooltip] = useState<boolean>(false);

  const COPY_TOOLTIP_TIMEOUT = 1000;

  const handleCopyLink = (url: string | undefined): void => {
    if (url) {
      navigator.clipboard.writeText(url);
      setShowCopiedTooltip(true);
      setTimeout(() => {
        setShowCopiedTooltip(false);
      }, COPY_TOOLTIP_TIMEOUT);
    }
  };

  const handleToggleRecipients = (index: number): void => {
    if (expandedEmail === index) {
      setExpandedEmail(-1);
    } else {
      setExpandedEmail(index);
    }
  };

  return (
    <div>
      <div className="flex gap-2 items-center ">
        <ActivitiesIcon />
        <Typography variant="heading18" color="color-black-100">
          Latest Activities
        </Typography>
      </div>
      <div
        className={`pb-2 pt-6 max-h-[280px] overflow-y-auto scrollbar-none ${
          emailHistory.length > 3
            ? 'shadow-[0_-10px_6px_-11px_rgba(0,0,0,0.1)_inset]'
            : ''
        }`}
      >
        {emailHistory.map((emailEvent, index) => (
          <div
            className={`border-l-2 border-[${colors['color-steel-80']}]/50  pl-[0.438rem] ml-[0.438rem] relative `}
          >
            <DotIcon
              className="absolute -left-[0.563rem] top-[0.563rem]"
              fill={
                emailEvent.trigger_event
                  ? colors['color-red-90']
                  : colors['color-blue-40']
              }
            />
            <div className="flex justify-between items-center pl-2 pt-2 h-6">
              <Typography
                variant="paragraph12"
                color={emailEvent.trigger_event ? 'color-red-160' : 'color-black-100'}
              >
                {emailEvent.message}
              </Typography>
              <div>
                {showVendorCheckoutUrl &&
                  canCopyLink &&
                  emailEvent.url &&
                  index === 0 && (
                    <div className="relative">
                      <div
                        className={`${
                          showCopiedTooltip ? 'block scale-100' : 'hidden scale-0'
                        } absolute top-[-20px] left-0 bg-[${
                          colors['color-gray-160']
                        }] rounded-md px-2 py-1 transition-transform duration-600 ease-in-out transform origin-bottom-left`}
                      >
                        <Typography variant="paragraph12" color="color-white-100">
                          Copied
                        </Typography>
                      </div>
                      <Button
                        size="x-small"
                        backgroundColor="none"
                        startIcon={<CopyIcon />}
                        onClick={() => handleCopyLink(emailEvent.url)}
                      >
                        Copy link
                      </Button>
                    </div>
                  )}
              </div>
            </div>
            <div className="pl-2 pt-2 pb-6">
              {emailEvent?.recipients?.length > 1 ? (
                <div>
                  <button
                    className="flex items-center gap-0.5"
                    onClick={() => handleToggleRecipients(index)}
                  >
                    <Typography variant="paragraph12" color="color-blue-120" bold>
                      Email & SMS Recipients
                    </Typography>
                    <ChevronIcon
                      className={` transition-transform duration-500 ease-in-out ${
                        expandedEmail === index ? '-rotate-90' : ''
                      }`}
                    />
                  </button>
                  <div
                    className={` transition-[max-height] overflow-hidden ease-in-out duration-500 pt-2 ${
                      expandedEmail === index ? 'max-h-96' : 'max-h-0'
                    }`}
                  >
                    <div className="py-4 px-6 bg-vartana-gray-20 rounded-lg my-1">
                      {emailEvent.recipients.map((recipient) => (
                        <div className="flex items-center gap-2 pb-0.5">
                          <ContactTooltipElement
                            email={recipient.email}
                            phone={recipient.phone}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-2 pb-0.5">
                  {emailEvent.recipients.map((recipient) => (
                    <Typography variant="paragraph12" color="color-gray-140">
                      <ContactTooltipElement
                        email={recipient.email}
                        phone={recipient.phone}
                      />
                    </Typography>
                  ))}
                </div>
              )}
              <Typography variant="paragraph12" color="color-gray-110">
                {formatTimestamp(emailEvent.actual_timestamp, emailEvent.timestamp)}
              </Typography>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default ActivitiesCard;
